import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, Activity, Wand2 } from "lucide-react";

/**
 * AudioQualityMeter — pro-grade broadcast audio diagnostics.
 *
 * Why
 * ---
 * The LED level meter only tells hosts how *loud* their mic is. It does not
 * tell them whether the signal is *clean*. A creator can be at a healthy
 * level while still clipping on consonants, sitting in a noisy room, or
 * leaking acoustic feedback into the stream. This panel surfaces five
 * quality signals so the host can fix audio issues *before* viewers complain.
 *
 * Metrics
 * -------
 *  - Clipping        : peak sample within last 250ms hits >= -0.5 dBFS
 *  - Noise floor     : 10th-percentile RMS over a 5s rolling window (dB)
 *  - SNR             : 90th-percentile RMS - 10th-percentile RMS (dB)
 *  - Mic dropout     : RMS below -70 dB for > 2s continuously
 *  - Echo / feedback : a single freq bin sits >22 dB above bin-mean for >500ms
 *
 * The component renders a compact status badge by default; clicking expands
 * the diagnostics panel. Status colour reflects the *worst* live issue so
 * hosts get a glance-able health indicator.
 *
 * Implementation notes
 * --------------------
 * Single AnalyserNode, single rAF loop. Reuses the global AudioContext so we
 * don't pay the latency penalty of a second context (matches AudioLevelMeter).
 * All percentile / rolling-window math is O(N) on a fixed-size ring buffer.
 */

let _ctx = null;
function ctx() {
  if (!_ctx) _ctx = new (window.AudioContext || window.webkitAudioContext)({ latencyHint: "interactive" });
  if (_ctx.state === "suspended") _ctx.resume().catch(() => {});
  return _ctx;
}

// ~20 samples/sec for 5s = 100-sample ring buffer. Enough resolution for
// percentile-based noise/SNR estimation without GC pressure.
const RING_SIZE = 100;
const SAMPLE_INTERVAL_MS = 50;

function percentile(sortedArr, p) {
  if (sortedArr.length === 0) return -90;
  const idx = Math.max(0, Math.min(sortedArr.length - 1, Math.floor(p * sortedArr.length)));
  return sortedArr[idx];
}

export default function AudioQualityMeter({
  stream,
  on = true,
  className = "",
  autoFixOn = false,
  onToggleAutoFix,
  getAutoFixStatus,
}) {
  const [expanded, setExpanded] = useState(false);
  const [metrics, setMetrics] = useState({
    clipping: false,
    noiseDb: -90,
    snrDb: 0,
    dropout: false,
    feedback: false,
    silentMs: 0,
  });
  // Live mirror of the Auto-Fix internal status — refreshed on the same
  // 50 ms cadence as the metrics so the Limit/Gate/Notch pills feel real-time.
  const [autoFixStatus, setAutoFixStatus] = useState(null);

  const rafRef = useRef(null);
  const sampleTimerRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const ringRef = useRef([]);
  const silentStartRef = useRef(null);
  const feedbackStartRef = useRef(null);
  const peakRef = useRef({ value: 0, until: 0 });

  useEffect(() => {
    if (!stream || !on) {
      setMetrics({ clipping: false, noiseDb: -90, snrDb: 0, dropout: false, feedback: false, silentMs: 0 });
      ringRef.current = [];
      silentStartRef.current = null;
      feedbackStartRef.current = null;
      return;
    }
    const audioTrack = stream.getAudioTracks?.()[0];
    if (!audioTrack || audioTrack.readyState === "ended") return;

    let cancelled = false;
    let ac;
    try {
      ac = ctx();
      const src = ac.createMediaStreamSource(stream);
      const analyser = ac.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.6;
      src.connect(analyser);
      sourceRef.current = src;
      analyserRef.current = analyser;
    } catch {
      return;
    }

    const timeBuf = new Float32Array(analyserRef.current.fftSize);
    const freqBuf = new Float32Array(analyserRef.current.frequencyBinCount);

    // High-frequency sample loop: tracks instantaneous peak for clipping.
    const tick = () => {
      if (cancelled) return;
      const a = analyserRef.current;
      if (!a) { rafRef.current = requestAnimationFrame(tick); return; }
      a.getFloatTimeDomainData(timeBuf);
      let peak = 0;
      for (let i = 0; i < timeBuf.length; i++) {
        const v = Math.abs(timeBuf[i]);
        if (v > peak) peak = v;
      }
      const now = performance.now();
      // Hold peak for 250ms — short enough to feel real-time, long enough
      // for the eye to register a transient hit.
      if (peak >= peakRef.current.value || now > peakRef.current.until) {
        peakRef.current = { value: peak, until: now + 250 };
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    // Slower sample loop: feeds the rolling ring buffer for percentile math
    // and runs the echo / feedback / dropout heuristics.
    sampleTimerRef.current = setInterval(() => {
      if (cancelled) return;
      const a = analyserRef.current;
      if (!a) return;

      // RMS in dB over last fftSize samples
      a.getFloatTimeDomainData(timeBuf);
      let sum = 0;
      for (let i = 0; i < timeBuf.length; i++) sum += timeBuf[i] * timeBuf[i];
      const rms = Math.sqrt(sum / timeBuf.length);
      const db = rms > 0 ? Math.max(-90, 20 * Math.log10(rms)) : -90;

      // Push into ring
      const ring = ringRef.current;
      ring.push(db);
      if (ring.length > RING_SIZE) ring.shift();

      // Percentile-based noise floor + SNR
      const sorted = [...ring].sort((x, y) => x - y);
      const noiseDb = percentile(sorted, 0.1);
      const speechDb = percentile(sorted, 0.9);
      const snrDb = Math.max(0, speechDb - noiseDb);

      // Dropout: RMS sub -70 dB sustained > 2s
      const now = performance.now();
      if (db < -70) {
        if (silentStartRef.current == null) silentStartRef.current = now;
      } else {
        silentStartRef.current = null;
      }
      const silentMs = silentStartRef.current ? now - silentStartRef.current : 0;
      const dropout = silentMs > 2000;

      // Echo / feedback: a single FFT bin sustained >22 dB above mean for >500ms.
      // Feedback is characterised by a narrow-band tone that "rings" louder
      // than surrounding bins — perfect signature for FFT peak-vs-mean.
      a.getFloatFrequencyData(freqBuf);
      let mean = 0;
      let max = -Infinity;
      let validCount = 0;
      // Skip first 4 bins (DC + sub-bass rumble) and last 25% (above 11kHz
      // is mostly headroom and noise — false positives there).
      const start = 4;
      const end = Math.floor(freqBuf.length * 0.75);
      for (let i = start; i < end; i++) {
        const v = freqBuf[i];
        if (Number.isFinite(v) && v > -100) {
          mean += v;
          validCount++;
          if (v > max) max = v;
        }
      }
      mean = validCount > 0 ? mean / validCount : -90;
      const peakAboveMean = max - mean;
      if (peakAboveMean > 22 && max > -45) {
        if (feedbackStartRef.current == null) feedbackStartRef.current = now;
      } else {
        feedbackStartRef.current = null;
      }
      const feedback = feedbackStartRef.current != null && (now - feedbackStartRef.current) > 500;

      // Clipping: peak in last 250ms hit -0.5 dBFS or above
      const clipping = peakRef.current.value >= 0.944; // 10^(-0.5/20) ≈ 0.944

      setMetrics({ clipping, noiseDb, snrDb, dropout, feedback, silentMs });

      // Mirror Auto-Fix status onto state so the Limit/Gate/Notch pills
      // re-render as the underlying processor reacts.
      if (autoFixOn && typeof getAutoFixStatus === "function") {
        try { setAutoFixStatus(getAutoFixStatus()); } catch {}
      }
    }, SAMPLE_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (sampleTimerRef.current) clearInterval(sampleTimerRef.current);
      try { sourceRef.current?.disconnect(); } catch {}
      try { analyserRef.current?.disconnect(); } catch {}
      sourceRef.current = null;
      analyserRef.current = null;
      ringRef.current = [];
      silentStartRef.current = null;
      feedbackStartRef.current = null;
    };
  }, [stream, on, autoFixOn, getAutoFixStatus]);

  // Determine overall health badge — worst-issue wins.
  const { clipping, noiseDb, snrDb, dropout, feedback } = metrics;
  let status = "excellent"; // excellent | good | warn | bad
  let statusLabel = "HD Audio";
  if (dropout || feedback || clipping) {
    status = "bad";
    statusLabel = dropout ? "Mic Silent" : feedback ? "Feedback!" : "Clipping!";
  } else if (snrDb < 12 || noiseDb > -45) {
    status = "warn";
    statusLabel = "Noisy";
  } else if (snrDb < 20) {
    status = "good";
    statusLabel = "Good";
  }

  const statusStyles = {
    excellent: "bg-emerald-500/20 text-emerald-300 border-emerald-400/40",
    good:      "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
    warn:      "bg-amber-500/20 text-amber-200 border-amber-400/40",
    bad:       "bg-rose-500/25 text-rose-100 border-rose-400/50 animate-pulse",
  };
  const StatusIcon = status === "excellent" || status === "good" ? CheckCircle2 : status === "warn" ? Activity : AlertTriangle;

  return (
    <div data-testid="audio-quality-meter" className={`inline-block ${className}`}>
      <button
        type="button"
        data-testid="audio-quality-toggle"
        onClick={() => setExpanded(v => !v)}
        title="Audio diagnostics — click to expand"
        className={`inline-flex items-center gap-1.5 px-2 h-8 rounded-md border text-xs font-semibold transition ${statusStyles[status]}`}
      >
        <StatusIcon className="w-3.5 h-3.5" />
        <span data-testid="audio-quality-status">{statusLabel}</span>
        {expanded ? <ChevronUp className="w-3 h-3 opacity-70" /> : <ChevronDown className="w-3 h-3 opacity-70" />}
      </button>

      {expanded && (
        <div
          data-testid="audio-quality-panel"
          className="absolute z-30 mt-2 w-64 rounded-lg border border-white/15 bg-zinc-950/95 backdrop-blur p-3 text-[11px] text-white/85 shadow-2xl"
        >
          <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">Audio Diagnostics</div>

          <Row label="Clipping" value={clipping ? "Yes — turn down mic" : "Clean"} bad={clipping} testid="aq-clipping" />
          <Row
            label="Noise Floor"
            value={`${noiseDb.toFixed(1)} dB`}
            warn={noiseDb > -45}
            testid="aq-noise"
            hint={noiseDb > -45 ? "Background hiss — find a quieter spot" : "Quiet room"}
          />
          <Row
            label="SNR"
            value={`${snrDb.toFixed(1)} dB`}
            warn={snrDb < 20}
            bad={snrDb < 12 && snrDb > 0}
            testid="aq-snr"
            hint={snrDb >= 20 ? "Voice stands out clearly" : snrDb >= 12 ? "Speak up or move closer" : "Voice is buried in noise"}
          />
          <Row label="Mic Dropout" value={dropout ? "Mic dead >2s" : "Live"} bad={dropout} testid="aq-dropout" />
          <Row label="Echo / Feedback" value={feedback ? "Feedback ring" : "None"} bad={feedback} testid="aq-feedback" />

          {onToggleAutoFix && (
            <div className="mt-2 pt-2 border-t border-white/10">
              <button
                type="button"
                data-testid="audio-quality-autofix-toggle"
                onClick={onToggleAutoFix}
                className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md border text-[11px] font-semibold transition ${
                  autoFixOn
                    ? "bg-emerald-500/20 border-emerald-400/40 text-emerald-200"
                    : "bg-white/5 border-white/15 text-white/80 hover:bg-white/10"
                }`}
                title={autoFixOn ? "Auto-Fix is healing audio in real-time" : "Turn on Auto-Fix to limit clipping, gate noise, and notch feedback"}
              >
                <span className="inline-flex items-center gap-1.5">
                  <Wand2 className="w-3.5 h-3.5" />
                  Auto-Fix Audio
                </span>
                <span className={`text-[10px] uppercase tracking-wider ${autoFixOn ? "text-emerald-300" : "text-white/40"}`}>
                  {autoFixOn ? "On" : "Off"}
                </span>
              </button>
              {autoFixOn && autoFixStatus && (
                <div className="mt-1.5 grid grid-cols-3 gap-1 text-[10px]" data-testid="audio-quality-autofix-status">
                  <FixChip label="Limit"  active={!!autoFixStatus.clipReducing} testid="aqfx-limit" />
                  <FixChip label="Gate"   active={!!autoFixStatus.gateActive}   testid="aqfx-gate" />
                  <FixChip label={autoFixStatus.notchHz ? `Notch ${autoFixStatus.notchHz}Hz` : "Notch"} active={!!autoFixStatus.notchHz} testid="aqfx-notch" />
                </div>
              )}
            </div>
          )}

          <div className="mt-2 pt-2 border-t border-white/10 text-[10px] text-white/40 leading-snug">
            Tip: keep the LED meter in green/amber and SNR above 20 dB for HD-grade audio.
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, bad = false, warn = false, hint, testid }) {
  const tone = bad ? "text-rose-300" : warn ? "text-amber-300" : "text-emerald-300";
  return (
    <div className="flex items-start justify-between gap-3 py-1" data-testid={testid}>
      <div className="flex flex-col">
        <span className="text-white/70">{label}</span>
        {hint && <span className="text-[10px] text-white/40 leading-tight">{hint}</span>}
      </div>
      <span className={`font-mono font-semibold ${tone}`}>{value}</span>
    </div>
  );
}

// Tiny pill that lights up while a particular Auto-Fix stage is engaged.
// Gives the host instant visual confirmation that Auto-Fix is doing
// something — important so they trust the toggle.
function FixChip({ label, active, testid }) {
  return (
    <span
      data-testid={testid}
      className={`text-center px-1.5 py-1 rounded font-mono uppercase tracking-wider transition ${
        active
          ? "bg-emerald-400/30 text-emerald-100 border border-emerald-300/40"
          : "bg-white/5 text-white/40 border border-white/10"
      }`}
    >
      {label}
    </span>
  );
}
